import { Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, XCircle, Lock, Target as TargetIcon } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

/**
 * Görev Motoru 2.0 — Faz 1: AI Arama Niyeti Kapsamı.
 * Renders the deterministic intent coverage matrix from the latest scan.
 *
 * Props:
 *  - coverage: array of {key,label,status,evidence_count,matched_keywords,recommendation}
 *  - score:    int (intent_score, 0-100)
 *  - visibleCap: -1 = all visible (paid), 1..N = how many revealed for free users
 */

const COPY = {
  tr: {
    eyebrow: "AI Arama Niyeti Kapsamı",
    headline: "Hangi aramalarda öne çıkıyorsunuz?",
    empty: "Niyet kapsamı bu analizde henüz oluşturulmamış. Yeni bir analiz başlatınca otomatik üretilecek.",
    scoreSub: "niyet kapsamı",
    statStrong: "Güçlü",
    statMedium: "Orta",
    statWeak: "Zayıf",
    statMissing: "Eksik",
    lockedTitle: (n) => `+${n} ek niyet kilitli`,
    lockedDesc: "Tüm AI niyet analizini görmek için paketini yükselt.",
    lockedCta: "Paketimi Yükselt",
  },
  en: {
    eyebrow: "AI Search Intent Coverage",
    headline: "Which searches are you showing up for?",
    empty: "Intent coverage has not been generated for this analysis yet. It will be generated automatically when you start a new analysis.",
    scoreSub: "intent coverage",
    statStrong: "Strong",
    statMedium: "Medium",
    statWeak: "Weak",
    statMissing: "Missing",
    lockedTitle: (n) => `+${n} more intents locked`,
    lockedDesc: "Upgrade your plan to see the full AI intent breakdown.",
    lockedCta: "Upgrade plan",
  },
};

const STATUS_META_BASE = {
  strong: { icon: CheckCircle2, color: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  medium: { icon: CheckCircle2, color: "text-cyan-300", bg: "bg-cyan-500/10", border: "border-cyan-500/25" },
  weak: { icon: AlertTriangle, color: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/25" },
  missing: { icon: XCircle, color: "text-rose-300", bg: "bg-rose-500/10", border: "border-rose-500/25" },
};

export default function IntentCoverageCard({ coverage, score, visibleCap = -1 }) {
  const { lang } = useLang();
  const t = COPY[lang] || COPY.tr;

  if (!coverage || coverage.length === 0) {
    return (
      <div className="rounded-3xl glass-strong p-6 border border-white/5" data-testid="intent-coverage-empty">
        <Header score={null} t={t} />
        <div className="text-sm text-slate-400 mt-4">
          {t.empty}
        </div>
      </div>
    );
  }

  const counts = coverage.reduce((acc, c) => ({ ...acc, [c.status]: (acc[c.status] || 0) + 1 }), {});
  const totalVisible = visibleCap === -1 ? coverage.length : Math.min(visibleCap, coverage.length);
  const visible = coverage.slice(0, totalVisible);
  const hiddenCount = coverage.length - visible.length;

  return (
    <div className="rounded-3xl glass-strong p-6 border border-white/5" data-testid="intent-coverage-card">
      <Header score={score} t={t} />

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <StatBox label={t.statStrong} count={counts.strong || 0} color="text-emerald-300" />
        <StatBox label={t.statMedium} count={counts.medium || 0} color="text-cyan-300" />
        <StatBox label={t.statWeak} count={counts.weak || 0} color="text-amber-300" />
        <StatBox label={t.statMissing} count={counts.missing || 0} color="text-rose-300" />
      </div>

      <div className="mt-5 grid gap-2.5 sm:grid-cols-2">
        {visible.map((c) => (
          <IntentChip key={c.key} item={c} t={t} />
        ))}
      </div>

      {hiddenCount > 0 && (
        <div
          className="mt-4 rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-4 flex items-center gap-3"
          data-testid="intent-coverage-locked"
        >
          <Lock size={14} className="text-cyan-300 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white">{t.lockedTitle(hiddenCount)}</div>
            <div className="text-xs text-slate-400">{t.lockedDesc}</div>
          </div>
          <Link
            to="/app/planlar"
            className="neon-btn rounded-lg px-3 py-1.5 text-xs text-white shrink-0"
            data-testid="intent-coverage-cta"
          >
            {t.lockedCta}
          </Link>
        </div>
      )}
    </div>
  );
}

function Header({ score, t }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-purple-400 mb-1">
          <TargetIcon size={11} /> {t.eyebrow}
        </div>
        <h3 className="font-display text-2xl font-semibold text-white">
          {t.headline}
        </h3>
      </div>
      {score !== null && (
        <div className="text-right shrink-0" data-testid="intent-coverage-score">
          <div className="text-3xl font-bold text-white leading-none">{score}<span className="text-slate-500 text-lg">/100</span></div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{t.scoreSub}</div>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, count, color }) {
  return (
    <div className="rounded-xl bg-black/30 border border-white/5 px-3 py-2.5">
      <div className={`text-2xl font-bold ${color} leading-none`}>{count}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function IntentChip({ item, t }) {
  const meta = STATUS_META_BASE[item.status] || STATUS_META_BASE.missing;
  const Icon = meta.icon;
  const statusLabel = ({
    strong: t.statStrong, medium: t.statMedium, weak: t.statWeak, missing: t.statMissing,
  })[item.status] || t.statMissing;
  return (
    <div
      className={`rounded-xl border ${meta.border} ${meta.bg} p-3`}
      data-testid={`intent-chip-${item.key}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-sm text-white font-medium truncate">{item.label}</div>
        <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider ${meta.color}`}>
          <Icon size={10} /> {statusLabel}
        </div>
      </div>
      <div className="text-[11px] text-slate-400 leading-snug">{item.recommendation}</div>
      {item.matched_keywords?.length > 0 && (
        <div className="mt-1.5 text-[10px] text-slate-500 truncate">
          {t.eyebrow === "AI Search Intent Coverage" ? "Matched cues" : "Bulunan ipuçları"}: {item.matched_keywords.slice(0, 3).join(", ")}
        </div>
      )}
    </div>
  );
}
